import PropTypes from "prop-types";
import usePageSecurity from "../../../../Utils/Helpers/usePageSecurity";
import {
  getAccess,
  pageSecurityActions,
} from "../../../../Utils/Helpers/pageSecurity";

/*
 * Declarative gate for an Add / Edit / Delete control.
 *
 *   <A_SecuredAction permission="update">
 *     <IconButton onClick={handleEdit}><Edit /></IconButton>
 *   </A_SecuredAction>
 *
 * Renders `fallback` (nothing by default) when the current page's security
 * does not grant the permission. `children` may also be a function, which is
 * handed the resolved access object for call sites that need to gate part of
 * a larger expression.
 */
const A_SecuredAction = (props) => {
  const { permission, currentPage, security, fallback, children } = props;
  const pageAccess = usePageSecurity(currentPage);
  const access = security ? getAccess(security) : pageAccess;

  const isAllowed = {
    [pageSecurityActions.READ]: access.canRead,
    [pageSecurityActions.CREATE]: access.canCreate,
    [pageSecurityActions.UPDATE]: access.canUpdate,
    [pageSecurityActions.DELETE]: access.canDelete,
    [pageSecurityActions.FULL]: access.hasFullAccess,
  }[permission];

  if (!isAllowed) {
    return fallback;
  }

  return typeof children === "function" ? children(access) : children;
};

A_SecuredAction.defaultProps = {
  permission: pageSecurityActions.UPDATE,
  fallback: null,
};

A_SecuredAction.propTypes = {
  permission: PropTypes.oneOf(Object.values(pageSecurityActions)),
  currentPage: PropTypes.object,
  /* Gate on an explicit { Read, Update, Full } block (e.g. a layout's
     templateSecurityUI) instead of the active page's security. */
  security: PropTypes.object,
  fallback: PropTypes.node,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

export default A_SecuredAction;
